import { Box, Checkbox, Divider, FormControl, FormControlLabel, MenuItem, Select, Switch } from "@mui/material"
import { AddOutlined } from "@mui-symbols-material/w300"
import React, { useState } from "react"
import {
  type Control,
  Controller,
  type FieldArrayWithId,
  type FieldValues,
  type Path,
  type PathValue,
  type UseFormReturn,
} from "react-hook-form"

import { Button } from "@/components/common/Button"
import { CancelTemplate, ModalContainer } from "@/components/common/Modals"
import MultiSelect from "@/components/MultiSelect/MultiSelect"
import { FIELD_LABEL_CLASSES, SELECT_STYLING_PROPS } from "@/constants/forms"
import { type Contact, ContactRole, type Location } from "@/graphql/codegen/graphql"
import type { ContactLocationAddFormValues, ContactLocationEditFormValues } from "@/schemas/contactFormSchema"
import { PrimaryContactDialogType } from "@/types/contacts"
import { formatLocationAddress } from "@/utils/utils"

export interface AddContactLocationRolesFormFieldsProps<T extends FieldValues> {
  fieldArrayName: Path<T>
  fields: FieldArrayWithId[]
  control: Control<T>
  locations: Location[]
  contactId?: string
  contactName: string
  methods: UseFormReturn<T>
  currentLocationRoles: ContactLocationEditFormValues[]
  newLocationRoles: ContactLocationAddFormValues[]
  onAdd: () => void
}

const getFieldPaths = <T extends FieldValues>(fieldArrayName: Path<T>, index: number) => ({
  locationId: `${fieldArrayName}.${index}.locationId` as Path<T>,
  roles: `${fieldArrayName}.${index}.roles` as Path<T>,
  isPrimary: `${fieldArrayName}.${index}.isPrimary` as Path<T>,
})

export const AddContactLocationRolesFormFields = <T extends FieldValues>({
  fieldArrayName,
  fields,
  control,
  locations,
  newLocationRoles,
  currentLocationRoles,
  contactId,
  contactName,
  methods,
  onAdd,
}: AddContactLocationRolesFormFieldsProps<T>): React.ReactNode => {
  const [showDialog, setShowDialog] = useState<PrimaryContactDialogType | null>(null)

  const [currentPrimaryContact, setCurrentPrimaryContact] = useState<Contact | null>(null)

  const [selectedContactLocation, setSelectedContactLocation] = useState<{
    fieldIndex: number
    isPrimary: boolean
  } | null>(null)

  const isLocationDisabled = (locationId: string, index: number) =>
    currentLocationRoles.some((item) => item.locationId === locationId) ||
    newLocationRoles.some((item, i) => item.locationId === locationId && i !== index)

  const handleTogglePrimary = (fieldIndex: number, isPrimary: boolean) => {
    const formArrayValues = methods.getValues(fieldArrayName) as (
      | ContactLocationAddFormValues
      | ContactLocationEditFormValues
    )[]

    const primaryContactToReplace = locations.find(
      (location) => location.locationId === formArrayValues?.[fieldIndex]?.locationId
    )?.primaryContact

    setCurrentPrimaryContact(primaryContactToReplace ?? null)

    if (primaryContactToReplace && isPrimary && contactId !== primaryContactToReplace.contactId) {
      setSelectedContactLocation({
        fieldIndex,
        isPrimary,
      })

      setShowDialog(PrimaryContactDialogType.REPLACE_PRIMARY_WARNING)
      return
    }

    const fieldPaths = getFieldPaths<T>(fieldArrayName, fieldIndex)
    methods.setValue(fieldPaths.isPrimary, isPrimary as PathValue<T, Path<T>>)
  }

  const handleConfirmToggle = () => {
    if (selectedContactLocation) {
      const { fieldIndex, isPrimary } = selectedContactLocation

      const fieldPaths = getFieldPaths<T>(fieldArrayName, fieldIndex)
      methods.setValue(fieldPaths.isPrimary, isPrimary as PathValue<T, Path<T>>)
    }

    setShowDialog(null)
  }

  return (
    <Box className='space-y-4'>
      {fields.map((field, index) => {
        const fieldPaths = getFieldPaths<T>(fieldArrayName, index)

        return (
          <Box key={field.id}>
            {index > 0 && <Divider className='mt-6' />}
            <Controller
              name={fieldPaths.locationId}
              control={control}
              render={({ field }) => (
                <FormControl fullWidth className='mt-6'>
                  <label className={FIELD_LABEL_CLASSES}>Location</label>
                  <Select {...field} {...SELECT_STYLING_PROPS}>
                    {locations.map((location) => {
                      const disabled = isLocationDisabled(location.locationId, index)
                      return (
                        <MenuItem key={location.locationId} value={location.locationId} disabled={disabled}>
                          {formatLocationAddress(location)}
                        </MenuItem>
                      )
                    })}
                  </Select>
                </FormControl>
              )}
            />
            <Controller
              name={fieldPaths.roles}
              control={control}
              render={({ field }) => (
                <Box className='mt-6'>
                  <label className={FIELD_LABEL_CLASSES}>Roles</label>
                  <MultiSelect
                    id={fieldPaths.roles}
                    value={field.value || []}
                    onChange={(selectedValues) => field.onChange(selectedValues)}
                    options={Object.keys(ContactRole).map((roleKey) => {
                      const roleValue = ContactRole[roleKey as keyof typeof ContactRole]
                      return {
                        value: roleValue,
                        label: roleValue,
                      }
                    })}
                    MenuProps={{
                      PaperProps: {
                        style: { maxHeight: 300 },
                      },
                    }}
                    children={Object.keys(ContactRole).map((roleKey) => {
                      const roleValue = ContactRole[roleKey as keyof typeof ContactRole]
                      const isChecked = field.value?.includes(roleValue)
                      return (
                        <MenuItem key={roleKey} value={roleValue}>
                          <Checkbox checked={!!isChecked} />
                          {roleValue}
                        </MenuItem>
                      )
                    })}
                  />
                </Box>
              )}
            />
            <Box className='mt-6 flex w-full items-center justify-between'>
              <Controller
                name={fieldPaths.isPrimary}
                control={control}
                render={({ field }) => (
                  <Box className='relative'>
                    <FormControlLabel
                      control={
                        <Switch
                          {...field}
                          checked={!!field.value}
                          onChange={(e) => handleTogglePrimary(index, e.target.checked)}
                          size='small'
                        />
                      }
                      label='Location Primary Contact'
                    />
                  </Box>
                )}
              />

              {index === fields.length - 1 && (
                <Button variant='primary' appearance='text' size='small' startIcon={<AddOutlined />} onClick={onAdd}>
                  Add Another Location and Role(s)
                </Button>
              )}
            </Box>
          </Box>
        )
      })}

      {showDialog === PrimaryContactDialogType.REPLACE_PRIMARY_WARNING && (
        <ModalContainer isOpen={true} onClose={() => setShowDialog(null)}>
          <CancelTemplate
            title='Are you sure you want to change the primary contact for this location?'
            message={`${contactName} will become the primary contact in place of ${currentPrimaryContact?.firstName} ${currentPrimaryContact?.lastName} by continuing.`}
            confirmLabel='Proceed'
            closeLabel='Cancel'
            onClose={() => setShowDialog(null)}
            onConfirm={handleConfirmToggle}
          />
        </ModalContainer>
      )}
    </Box>
  )
}
